import type { Lang } from '../i18n/languages'

// --- Rules Chat ---

export interface CitedRule {
  rule_number: string
  rule_text: string
}

export interface ReferencedCard {
  name: string
  oracle_text: string
}

export interface RulesChatRequest {
  question: string
  conversation_id?: string
  card_names?: string[]
  /** Client-resolved canonical card names (autocomplete or photo). Bypasses server-side extraction. */
  referenced_card_names?: string[]
  /** User-pasted oracle text for cards not in Scryfall. Map of card_name -> oracle_text. */
  manual_card_text?: Record<string, string>
}

export interface IdentifyCardResponse {
  name: string
  oracle_text?: string
  type_line?: string
  image_uri?: string
}

export interface RulesChatResponse {
  answer: string
  summary: string
  confidence: string
  cited_rules: CitedRule[]
  cards_referenced: ReferencedCard[]
  conversation_id: string
}

// --- Validation ---

export interface ValidationError {
  code: string
  message: string
  severity: string
  cards?: string[]
  /** card name -> Scryfall oracle id, on CARD_BANNED / CARD_NOT_LEGAL issues
   *  (additive; a card without a known id is absent, older payloads omit it). */
  oracle_ids?: Record<string, string>
  details?: Record<string, unknown>
}

export interface ValidationResult {
  valid: boolean
  errors: ValidationError[]
  // The API sends issue objects (severity "warning", e.g. CARD_NOT_LEGAL with
  // its cards); bare strings are older saved blobs.
  warnings: Array<string | ValidationError>
  card_count: number
  has_commanders?: boolean
  commander_count?: number
  schema_version?: string
}

/** Per-bracket cohort curve, normalized to percentages that sum to ~100. */
export interface CohortBracketCurve {
  curve_pct: Record<string, number>
  avg_mv: number
  n: number
}

/**
 * Additive `deck_stats.composition` block (API Phase 2). Absent on every
 * response the API served before it shipped, and on every saved/shared deck
 * blob stored before then, so every consumer must treat it as optional.
 *
 * Shape mirrors the captured wire response verbatim (see
 * `__tests__/compositionFixture.json`, which IS the contract):
 * - `curve.buckets` is keyed "0".."6" plus "7+", nonland and quantity
 *   weighted. The "0" key and `curve.by_type` shipped later than the rest:
 *   older responses/blobs lack both, and fold MV 0 into bucket "1".
 * - `curve.by_type` maps each bucket to single-category type counts (each
 *   card classified exactly once), so stacks sum to the bucket totals.
 * - `pips` is W/U/B/R/G only (no C - lands and generic costs make no pips).
 * - `sources` adds "C" for colorless producers.
 * - `cohorts` is still served by the API but no longer rendered (the
 *   "Typical Bracket N" overlay was removed in the curve redesign).
 */
export interface DeckComposition {
  curve: {
    buckets: Record<string, number>
    /** Additive: per-bucket single-category type counts for the stacked
     *  curve. Absent on responses/blobs from before it shipped. */
    by_type?: Record<string, Record<string, number>>
    total: number
  }
  avg_mv: number
  median_mv: number
  pips: Record<string, number>
  sources: Record<string, number>
  /** Present on every API response, but old saved/shared blobs may lack it -
   *  consumers must not assume it. */
  type_counts?: Record<string, number>
  /** Spell // land modal DFCs (e.g. Turntimber Symbiosis). `type_counts.land`
   *  stays front-face only, so lands as a deckbuilder counts them are
   *  `type_counts.land + mdfc_land_count`. Absent on older responses. */
  mdfc_land_count?: number
  price_total_usd: number
  top_prices: { name: string; usd: number }[]
  cohorts?: {
    corpus_size: number
    brackets: Record<string, CohortBracketCurve>
  }
}

/** Additive deck_stats.report block (Deck Report stat pack). Absent on
 * every payload predating it and on degraded data - all consumers null-gate. */
export interface DeckReportConsistency {
  p_lands_t2: number
  p_lands_t3: number
  p_lands_t4: number
  p_ramp_t2: number
  p_ramp_t3: number
  p_two_ramp_t3: number
  p_land_and_ramp_t2: number
  p_keepable_opener: number
  /** Absent when the commander wasn't resolved/matched in the card list -
   *  the server drops both commander keys rather than ship a false "0%"
   *  (a genuine 0.0 for a matched commander does not happen in practice). */
  p_commander_on_curve?: number
  p_commander_colors_ok?: number
  expected_opening_lands: number
}

export interface OneCardAwayCombo {
  combo_name: string
  have: string[]
  missing: string[]
  produces: string[]
  power_level: string
}

/** Additive report.advice block (gameplay advice, facts-only v1). All copy
 * is client-side, keyed on the closed code vocabularies; unknown codes
 * must render nothing, never the raw token. */
export interface AdviceHoldItem {
  name: string
  reason_code: string
  timing_code: string
}

export interface DeckAdviceMulligan {
  land_range: { min: number; max: number }
  needs_accelerant_below?: number
  accelerant_examples?: string[]
  commander_castable_by?: number
  p_keepable: number
}

export interface DeckAdvice {
  mulligan?: DeckAdviceMulligan
  hold_priority?: AdviceHoldItem[]
}

export interface DeckReport {
  consistency?: DeckReportConsistency
  combos?: { one_card_away: OneCardAwayCombo[] }
  advice?: DeckAdvice
}

export interface DeckStats {
  total_cards: number
  cards_found: number
  cards_not_found: string[]
  /** Additive; absent on pre-Phase-2 responses and older saved decks. */
  composition?: DeckComposition | null
  /** Additive; absent on pre-Deck-Report responses and older saved decks. */
  report?: DeckReport | null
}

export interface EngineAnalysis {
  primary_engine: string
  /** Localized display name, a sibling the API sends ONLY on a non-English
   *  response (i18n 2e). Render-only: `primary_engine` stays the identity. */
  primary_engine_display?: string
  engine_confidence: number
  secondary_engines: string[]
  /**
   * How many of the deck's nonland cards contribute to the chosen engine.
   * Optional: the server withholds it (along with `total_nonland_cards`)
   * whenever the count would describe a different engine than
   * `engine_signature_cards` does, so treat it and `total_nonland_cards`
   * as arriving or absent together.
   */
  engine_card_count?: number
  engine_density: number
  engine_indicators: string[]
  engine_display_name?: string
  /**
   * Source of the final engine label. Values: "heuristic" | "edhrec" |
   * "edhrec_affinity" | "engine_classifier_v1" | "mechanic_routing_override" |
   * "unified_scorer". Mirrors `strategy_source` for the engine axis. On the
   * strategy axis, the UI still uses `strategy_source` to surface a
   * "Validated against EDHREC community data" caption when an override
   * fired. The equivalent engine-axis caption was REMOVED 2026-08-09: the
   * same override condition (an edhrec_affinity override where
   * classifier_engine differed from the final pick) is now treated as a
   * hedge, i.e. our two detection stages disagreeing, and is surfaced by
   * the "Close call with X" chip instead of a "Validated" caption.
   */
  engine_source?: string
  /**
   * Raw classifier engine_v2 before deck-truth validation. Set when the
   * EDHREC affinity validator or the unified deck-truth scorer overrode
   * the classifier's pick. Used for the "Model guess: X" transparency line.
   */
  classifier_engine?: string
  /**
   * Top-N deck-truth engine scores (descending). Populated when the
   * engine validator runs. Currently surfaced for debugging/tooltips, not
   * yet rendered as visible chips - mirror of `strategy_scores`.
   */
  engine_scores?: Record<string, number>
  /**
   * Deck cards with the highest affinity for the chosen engine. Absent and
   * empty mean the same thing: nothing in the 99 backs the label, and the
   * server marks the result ambiguous when that happens.
   */
  engine_signature_cards?: string[]
  /**
   * Denominator for `engine_card_count`. See that field's doc: the two
   * always arrive together or not at all.
   */
  total_nonland_cards?: number
  /** Engine axis near-tie (top/second within the dominance threshold). The UI
   *  renders a "close call" treatment instead of false certainty. */
  is_ambiguous?: boolean
  runner_up?: string
  runner_up_display_name?: string
  /** Strategy axis near-tie (derived from strategy_source "near-tie"). */
  strategy_is_ambiguous?: boolean
  /** Server-authoritative strategy runner-up on a near-tie. Prefer this over
   *  re-sorting strategy_scores (rounding/tie order can diverge). */
  strategy_runner_up?: string
  primary_engine_legacy?: string
  engine_v2?: string
  sub_engine?: string
  strategy?: string
  /** Plain-English definition of the engine for end-user explanations. */
  engine_description?: string
  /** Plain-English definition of the strategy for end-user explanations. */
  strategy_description?: string
  /**
   * Phase 2 (2026-05-06): the raw ML classifier strategy guess before we
   * validated it against EDHREC theme affinity. Useful for debugging and
   * surfacing "model said X, validator picked Y" transparency.
   */
  classifier_strategy?: string
  /**
   * "classifier" - kept the model's guess (it had deck-truth support);
   * "edhrec_affinity" - replaced classifier with the top affinity scorer;
   * "fallback" - no deck-truth signal available, classifier passed through.
   */
  strategy_source?: 'classifier' | 'edhrec_affinity' | 'fallback'
  /**
   * Top deck-truth strategy scores from the EDHREC theme affinity index.
   * Sorted descending. Surfaced for the "Based on your 99 cards" panel and
   * for tooltips that explain which alternatives the deck supports.
   */
  strategy_scores?: Record<string, number>
  /**
   * Top deck cards with highest affinity for the validated strategy. Used to
   * show "Cards in your deck typical of '<strategy>' decks on EDHREC: A, B,
   * C" when no curated strategy_description is available. Quoted from the
   * affinity index, never generated.
   */
  strategy_signature_cards?: string[]
  tribe?: string | null
}

export interface DeckSignalsMechanic {
  category: string
  count: number
  /** Non-English responses only (i18n 2e); render-only, `category` is identity. */
  category_display?: string
}

/** New v2 shape for top_triggers_effects (no keywords mixed in). */
export interface DeckSignalsTriggerEffect {
  label: string
  count: number
  /** Non-English responses only (i18n 2e); render-only, `label` is identity. */
  label_display?: string
}

export interface DeckSignalsCreatureType {
  type: string
  count: number
  fraction: number
}

export interface DeckSignals {
  theme_tags: ThemeTag[]
  /** @deprecated Use top_triggers_effects. Kept for iOS compatibility. */
  dominant_mechanics: DeckSignalsMechanic[]
  /** Triggers + effect categories only. No keyword names like Flying mixed in. */
  top_triggers_effects?: DeckSignalsTriggerEffect[]
  role_breakdown: Record<string, number>
  creature_type_distribution: DeckSignalsCreatureType[]
}

/** New v2 shape for mechanics_detected with authoritative descriptions. */
export interface MechanicDetectedV2 {
  name: string
  count: number
  description: string | null
  source: 'comp_rules_glossary' | 'scryfall_reminder' | null
}

export interface ComboInfo {
  combo_name: string
  /** Older blobs used `name` instead of `combo_name`. */
  name?: string
  /** Server count of the pieces; absent on older blobs, fall back to cards.length. */
  card_count?: number
  cards: string[]
  description: string
  combo_type: string
  power_level: string
  colors: string[]
  status?: 'complete' | 'missing_requirements'
  missing_requirements?: string[]
  prerequisites?: string
  produces?: string[]
  mana_needed?: string
  /** `requires` templates the server could not resolve to concrete cards. */
  requires_unverified?: string[]
  /** Satisfied `requires` templates with the (<=3) deck cards satisfying them. */
  requires_satisfied?: { name: string; satisfied_by: string[] }[]
}

export interface ComboAnalysis {
  detected_combos: (string | ComboInfo)[]
  has_deterministic_win: boolean
  has_cedh_combo: boolean
  highest_power_level: string
  combo_density: number
  missing_pieces: Record<string, string[]>
}

export interface TribalAnalysis {
  primary_tribe: string | null
  secondary_tribes: string[]
  tribal_density: number
  is_tribal_deck: boolean
  lords_present: string[]
  payoffs_present: string[]
}

export interface RoleSummary {
  engine_enabler: number
  engine_multiplier: number
  engine_support: number
  payload: number
  combo_piece: number
  utility: number
}

export interface CardClassification {
  card_name: string
  primary_role: string
  secondary_roles: string[]
  engine_contribution: number
  is_cheap_enabler: boolean
  role_reasons: string[]
  type_line?: string
  quantity?: number
  /** EDHREC per-card synergy as a fraction (e.g. 0.39 = +39%). Overlaid from the
   *  builder response in buildAnalysisFromResult; null/absent for non-EDHREC cards. */
  synergy?: number
  /** otag-derived display bucket for the Full Card Breakdown's Function
   *  grouping (ramp/removal/card_draw/tutor/...). Absent on pre-rollout saved
   *  decks - callers must fall back to primary_role. */
  function_category?: string
}

/** One entry of `commander_synergy.high_synergy_cards`. `score` is EDHREC
 *  synergy as a fraction: a DELTA from the format average, not popularity.
 *  Format staples score near zero - see the Sol Ring rule in the spec. */
export interface HighSynergyCard {
  card: string
  score: number
  reasons: string[]
  role?: string
}

export interface ThemeTag {
  tag: string
  /** Non-English responses only (i18n 2e); render-only, `tag` is identity
   *  (a React key and the formatTag input). */
  tag_display?: string
  confidence: number
  card_count: number
  key_cards: string[]
}

export interface IpomAnalysis {
  engine: EngineAnalysis
  combos: ComboAnalysis
  tribal: TribalAnalysis
  card_classifications?: CardClassification[]
  role_summary: RoleSummary
  cheap_enabler_count: number
  combo_piece_count: number
  total_classified: number
  ipom_version: string
  theme_tags?: ThemeTag[]
  deck_signals?: DeckSignals
}

export interface NextBracketGuidance {
  current_bracket: number
  next_bracket: number
  missing_conditions: string[]
  example_card_categories: string[]
  explanation: string
}

export interface MLPrediction {
  median?: number
  bracket?: number
  sigma?: number
  confidence?: string
}

export interface ClassificationConfidence {
  level: string
  reason: string
}

export type LockStateBasis = 'none' | 'combo' | 'cumulative_soft' | 'commander'

export interface BracketDetermination {
  engine: {
    name: string
    type?: string
    sub_type?: string
    source: string
    confidence: number
  }
  bracket: {
    level: number
    // API emits these from bracket_classifier.py determination block.
    // `name` was an older shape we don't believe the server still emits;
    // kept optional for back-compat with any cached/serialized responses.
    determined_by?: string
    estimated_turn?: number
    gc_floor_applied?: boolean
    mld_floor_applied?: boolean
    extra_turns_floor_applied?: boolean
    name?: string
  }
  signals: {
    game_changers: number
    tutors: number
    tutor_weighted_score: number
    combos_detected: number
    combo_power: string
    fast_mana: number
    experience_warp?: number
    game_changer_cards?: string[]
    // Lock-state predictor fields (BRACKETS_SCHEMA_VERSION 5.1.0+).
    // Always present; clients should treat 0 / "none" as "no lock signal".
    warp_bracket?: number
    lock_warp_bracket?: number
    lock_state_basis?: LockStateBasis
    lock_state_reliability?: number
    // Bracket-speed merge-layer arbitration fields (mtg-api Tier 2.8 / 3.5,
    // shipping 2026-05-20). All optional: legacy responses + saved decks
    // analyzed before the surfacing don't carry them. Clients gate
    // rendering on presence.
    selected_reliability?: number
    final_bracket_basis?: FinalBracketBasis
    turn_estimate_basis?: TurnEstimateBasis
    ml_sigma?: number
    ml_estimate?: number
    displayed_bracket?: number
    rule_bracket?: number
  }
  data_sources: string[]
  why: string[]
  what_would_change: string[]
}

// Mirrors `_FINAL_BRACKET_BASIS_VALUES` in app/bracket_classifier.py.
// Tracks WHICH rule selected the bracket the user saw, after ML
// arbitration and floor application.
export type FinalBracketBasis =
  | 'ml'
  | 'heuristic'
  | 'warp_floor'
  | 'gc_floor'
  | 'combo_floor'
  | 'mld_floor'
  | 'extra_turns_floor'
  | 'b5_gate_downgrade'
  | 'exhibition_override'

// Which path produced the displayed turn estimate.
export type TurnEstimateBasis = 'ml' | 'heuristic'

export interface WarpAnalysis {
  warp_score: number
  warp_bracket: number
  warp_reasons: string[]
  has_significant_warp?: boolean
  lockout_count?: number
  mana_denial_count?: number
  extra_turn_count?: number
  extra_turn_cards?: string[]
  /** WotC's mass land denial list, the B4 floor input. */
  wotc_mld_count?: number
  wotc_mld_cards?: string[]
  free_interaction_count?: number
  classifier_stax_count?: number
  // Lock-state predictor surface (LOCK_STATE_WARP_V1).
  lock_warp_bracket?: number
  lock_state_basis?: LockStateBasis
  lock_state_reliability?: number
}

/**
 * Contents-vs-speed fit verdict (display-only, additive 2026-07).
 * - underpowered_for_floor: a contents floor (game changers / combo) forces
 *   the bracket above the deck's own speed - render as an amber warning.
 * - speed_rated: a B4+ rating driven purely by speed with unrestrictive
 *   contents - render as a neutral info line.
 * - matched: no tension - render nothing.
 */
export interface BracketFit {
  status: 'underpowered_for_floor' | 'speed_rated' | 'matched'
  contents_floor: number
  plays_like_bracket: number
  reasons: string[]
  detail: string
}

/** The content rules a `floor_summary` reason can name. The server may add
 *  kinds later, so clients render `text` for any kind and only key
 *  kind-specific behavior (finding chips, the combo badge) off known ones. */
export type FloorReasonKind = 'game_changers' | 'combo' | 'mass_land_denial' | 'extra_turns' | 'warp' | 'b5_gate'

export interface FloorSummaryReason {
  /** A FloorReasonKind today; typed open because the server may add kinds. */
  kind: string
  bracket: number
  /** Localized sentence, rendered as-is. */
  text: string
  /** Localized short chip for the matching finding. */
  chip: string
  /** Every card the reason names (all of them, not a first-3 cut). */
  cards: string[]
  card_count: number
}

/**
 * What set the bracket, when a content rule (or warp, or the B5 gate) did
 * rather than speed alone (additive, mtg-api `app/floor_summary.py`).
 * First-party only. The key is ABSENT when there is nothing to say, and a
 * `version` other than 1 must be ignored: read it through `floorSummaryOf`.
 */
export interface FloorSummary {
  version: 1
  /** Equals final_bracket. */
  bracket: number
  /** Equals determination.signals.final_bracket_basis. */
  basis: string
  plays_like_bracket: number
  /** Localized sentence, rendered as-is. */
  headline: string
  reasons: FloorSummaryReason[]
}

export interface BracketAnalysis {
  deck_bracket: number
  final_bracket?: number
  bracket_description: string
  bracket_reason: string
  estimated_win_turn: number | null
  speed_bracket: number
  warp_bracket: number
  bracket_determined_by?: string
  bracket_source?: string  // "ml" or "rule"
  ml_prediction?: MLPrediction
  engine_analysis: {
    primary_engine: string
    /** Localized display name, a sibling the API sends ONLY on a non-English
     *  response (i18n 2e). Render-only: `primary_engine` stays the identity. */
    primary_engine_display?: string
    engine_confidence: number
    secondary_engines: string[]
  }
  combo_analysis: {
    has_cedh_combo: boolean
    detected_combos: string[]
    highest_power_level: string
  }
  game_changers_found: string[]
  total_game_changers: number
  game_changers_by_category: Record<string, string[]>
  cedh_cards_found?: string[]
  total_cedh_cards?: number
  requires_disclosure: boolean
  fast_mana_count: number
  fast_mana_found: string[]
  tutor_count: number
  tutor_weighted_score?: number
  tutor_tier_breakdown?: Record<string, number>
  tutors_found?: string[]
  /** Per-card tier (tier_1|tier_2|tier_3|uncategorized) for the displayed
   *  tutors, so the Tutors section can chip Unconditional/Conditional/Narrow. */
  tutor_card_tiers?: Record<string, string>
  /** Counts-only aggregate ({unconditional, conditional, narrow}). */
  tutor_tier_summary?: { unconditional: number; conditional: number; narrow: number }
  has_turn_2_3_capability: boolean
  turn_breakdown?: Record<string, unknown>
  analysis_confidence?: Record<string, unknown>
  classification_confidence?: ClassificationConfidence
  bracket_fit?: BracketFit
  warp_analysis?: WarpAnalysis
  bracket_explanation?: string[]
  bracket_narrative?: string
  next_bracket_guidance?: NextBracketGuidance | null
  next_bracket_hints?: string[] | null
  determination?: BracketDetermination
  floor_summary?: FloorSummary
  /** The API's stateless share URL (`https://commanderbracket.app/bracket?deck=<blob>`).
   *  Absent on older responses and saved blobs. The deck poster reuses its
   *  blob so web and iOS ask /api/poster for the same URL (one CDN entry). */
  share_link?: string | null
}

/** Chip for a matched floor reason whose own `chip` is missing or blank, so a
 *  matched finding never shows an empty chip and a matching combo is still
 *  badged. Same label iOS uses. */
export const FLOOR_REASON_FALLBACK_CHIP = 'Sets the floor'

/** The bracket analysis's `floor_summary`, or null when it is absent, not
 *  version 1, or has no usable headline. Every object reason is KEPT (known
 *  kind or not) and normalized so callers can trust the shape: a missing or
 *  non-string `text` reads as '' (callers skip empty text where they render
 *  it), a blank `chip` reads as FLOOR_REASON_FALLBACK_CHIP, and `cards` keeps
 *  only its string entries. */
export function floorSummaryOf(ba: BracketAnalysis | undefined | null): FloorSummary | null {
  const fs = ba?.floor_summary
  if (!fs || typeof fs !== 'object' || fs.version !== 1) return null
  if (typeof fs.headline !== 'string' || !fs.headline.trim()) return null
  const reasons: FloorSummaryReason[] = (Array.isArray(fs.reasons) ? fs.reasons : [])
    .filter((r): r is FloorSummaryReason => !!r && typeof r === 'object' && !Array.isArray(r))
    .map(r => {
      const cards = Array.isArray(r.cards) ? r.cards.filter((c): c is string => typeof c === 'string') : []
      const chip = typeof r.chip === 'string' ? r.chip.trim() : ''
      return {
        ...r,
        kind: typeof r.kind === 'string' ? r.kind : '',
        text: typeof r.text === 'string' ? r.text.trim() : '',
        chip: chip || FLOOR_REASON_FALLBACK_CHIP,
        cards,
        card_count: typeof r.card_count === 'number' ? r.card_count : cards.length,
      }
    })
  return { ...fs, headline: fs.headline.trim(), reasons }
}

/** The first reason of `kind`, or undefined. */
export function floorReason(ba: BracketAnalysis | undefined | null, kind: FloorReasonKind): FloorSummaryReason | undefined {
  return floorSummaryOf(ba)?.reasons.find(r => r.kind === kind)
}

/** The bracket the server chose after ML confidence arbitration. */
export function displayBracket(ba: BracketAnalysis): number {
  return ba.final_bracket ?? ba.deck_bracket
}

export interface WinConditions {
  primary_win_condition: string
  has_combo_win: boolean
  has_combat_win: boolean
  win_condition_cards: Record<string, string>
  commander_combos: string[]
}

/** A single unowned card the API says the user still needs to acquire. */
export interface OwnershipBuyCard {
  name: string
  quantity: number
  price_usd?: number | null
}

/**
 * Server-computed ownership rollup returned additively on build responses when
 * a collection signal was in the request. Null/absent on legacy responses, on
 * mode "off" with no collection, and on older saved decks - callers must fall
 * back to client-side name matching in those cases. `cards_to_buy` is the
 * unowned non-basics (commander excluded), sorted price desc then name;
 * `upgrade_cost_usd` is the sum of those prices (None counted as 0).
 */
export interface OwnershipSummary {
  schema_version: string
  mode: 'off' | 'prefer' | 'only'
  owned_count: number
  total_count: number
  percent_owned: number
  owned_includes_basics: boolean
  commander_counted_owned: boolean
  cards_to_buy: OwnershipBuyCard[]
  upgrade_cost_usd: number
}

/** Additive deck_health block (API 2026-08-08). The KEY is always present
 *  on analyze-complete but NULLABLE (withheld decks carry null + one
 *  WARNING finding); absent entirely on legacy saved/shared blobs. */
export interface DeckHealthSpoke {
  key: string
  observed: number | null
  band: [number, number]
  score: number
  basis: 'bracket_engine' | 'bracket' | 'global'
  basis_n: number
  /** `mdfc` marks a spell // land modal DFC counted by the land_count spoke.
   *  Optional: older responses never carry it. */
  cards: { name: string; quantity: number; mdfc?: boolean }[]
  /** Plain-English "what this measures", served by the API so partner
   *  integrations get the same explanation. OPTIONAL because saved decks
   *  persist the health block: every blob written before 2026-08-19 has no
   *  such key, and those rows still render. */
  tooltip?: string | null
}

export interface DeckHealthPillar {
  score: number | null
  spokes: DeckHealthSpoke[]
  tooltip?: string | null
}

export interface DeckHealth {
  score: number | null
  weights: Record<string, number>
  pillars: Record<string, DeckHealthPillar>
  basis_note: {
    bracket: number | null
    engine: string | null
    degraded: string | null
    partner_basis_caveat?: boolean
  }
  /** How to read the scores at all (cohort-relative, two-sided). This panel
   *  deliberately does NOT render it - the gauge and the amber "above the
   *  typical 17-27.75" caption already say it better, and the footer already
   *  names the cohort. It ships for consumers that draw a bare score bar. */
  scoring_note?: string
}

/** Additive inclusion_gap block. KEY ABSENT when the commander is uncached
 *  or unresolved; present with empty cards when there are no gaps. Hide on
 *  absent OR empty. */
export interface InclusionGap {
  commander: string | null
  num_decks: number
  cards: { name: string; inclusion: number }[]
}

/** Additive archenemy block. KEY ABSENT when unavailable, never zeroed. */
export interface ArchenemyPattern {
  key: string
  severity: number
  value: number
  engine_driven: boolean
  reason: string
  cards: { name: string; quantity: number }[]
}

export interface Archenemy {
  score: number
  level: string
  levels: string[]
  /** Non-English responses only (i18n 2e). Render-only: `level` / `levels`
   *  stay the identity that levelIndex compares; `levels_display[i]` is the
   *  name of `levels[i]` (index-aligned). */
  level_display?: string
  levels_display?: string[]
  patterns: ArchenemyPattern[]
  basis_note: { corpus_n: number; semantics: 'events'; degraded: string | null }
}

export interface CompleteAnalysis {
  /** The language the analysis prose was rendered in, set by the API ONLY on
   *  a non-English response (mtg-api i18n 3-api.1). Absent means English.
   *  Never stamped by the client. */
  analysis_locale?: Exclude<Lang, 'en'>
  validation: ValidationResult
  deck_stats: DeckStats
  ipom_analysis: IpomAnalysis
  bracket_analysis: BracketAnalysis
  win_conditions: WinConditions
  mechanics_detected?: Record<string, number>
  mechanics_detected_v2?: MechanicDetectedV2[]
  strengths?: string[]
  weaknesses?: string[]
  schema_versions: Record<string, string>
  commander_analysis?: Record<string, unknown>
  commander_synergy?: Record<string, unknown>
  tcgplayer_ids?: Record<string, number>
  // Additive server ownership rollup; carried through save/load so saved-deck
  // buy surfaces can prefer it over client name matching. Absent on legacy data.
  ownership_summary?: OwnershipSummary | null
  deck_health?: DeckHealth | null
  inclusion_gap?: InclusionGap
  archenemy?: Archenemy
  /** Per-analysis id (12 lowercase hex) the API also writes to its analysis
   *  log. Echoed back on a feedback vote so the vote joins THIS analysis
   *  exactly. Absent on older API responses. */
  correlation_id?: string
  /** Client-stamped on every full save: analysisContentHash() of the decklist
   *  this analysis was computed from (main, commander and companion cards
   *  only). A deck whose stored list hashes differently has been edited since
   *  its last rating. Absent on rows saved before 2026-10; never sent by the
   *  API. */
  analyzed_content_hash?: string
  /** Client-stamped on a New deck (lib/deckDraft): the deck was saved with a
   *  real analysis of its partial contents, but the user has not analyzed it
   *  yet, so every surface reads "Not analyzed yet". Cleared by the first
   *  explicit Analyze (an API response never carries it). Old clients ignore
   *  it. */
  draft?: boolean
  /** The physical card count (command zone included) the draft was saved with. */
  draft_card_count?: number
}

// --- Card search (POST /cards/search) ---------------------------------------

/**
 * One result row. Fields the server has no value for are OMITTED, not nulled,
 * so `price_usd === undefined` means "we have no price", never "$0".
 */
export interface CardSearchCard {
  name: string
  cmc: number
  color_identity: string[]
  image: { small?: string; normal?: string; large?: string } | null
  mana_cost?: string
  type_line?: string
  oracle_text?: string
  price_usd?: number
  tcgplayer_id?: number
  /** Scryfall oracle id (additive; older servers omit it). decodeCard drops
      anything that is not a string. */
  oracle_id?: string
  /** Per-commander EDHREC context, same block the mana tune ships. Present
      only when the search carried a commander whose page we hold; results
      are then ordered by this synergy, on-page cards first. */
  edhrec?: {
    inclusion_pct: number | null
    synergy: number | null
    on_page: boolean
    line?: string | null
  } | null
}

export interface CardSearchResponse {
  // "syntax" = the query ran as written, no model involved. "translated" = a
  // plain-English ask the server turned into search syntax. "name" = the
  // local name / face / alias match (mode "name", card search v2), no model.
  source: 'syntax' | 'translated' | 'name'
  query: string
  // The query that actually ran, server-added colour identity and legality
  // included. Null on the syntax path, where `query` already is it.
  translated_query: string | null
  explanation: string | null
  count: number
  cards: CardSearchCard[]
  // Present only on a cache hit, so the absence of the key is not a claim.
  cached?: boolean
}

export interface CardSearchRequest {
  query: string
  commander?: string
  limit?: number
  order?: 'edhrec' | 'usd' | 'cmc'
}

// --- Universal search (GET /search/suggest, GET /rules/search) --------------
// Plan nav-search-2026-10-16, A1 and A2. Instant, no model call.

/** One card from `/search/suggest`. `name` is the canonical English name (also
 *  the display name in v1); `matched_alias` is true when the hit was an es/pt/ja
 *  alias rather than the name. The price is a representative printing's. */
export interface SearchSuggestItem {
  oracle_id: string
  name: string
  matched_alias: boolean
  /** True for a tier-0 hit: the query IS the canonical name, a face name or
   *  an alias. Optional: absent from an older API (the client then falls
   *  back to comparing the name and faces itself). */
  exact_match?: boolean
  type_line: string | null
  image_url: string | null
  /** `usd` is the representative printing's nonfoil price. `usd_from` is
   *  the lowest NONFOIL price across every printing of the card; optional
   *  because an older API does not send it. */
  price: { usd: number | null; usd_from?: number | null }
  price_note: string
}

export interface SearchSuggestResponse {
  items: SearchSuggestItem[]
}

/** One Comprehensive Rules hit (English corpus): a rule number or a glossary term. */
export interface RulesSearchItem {
  kind: 'rule' | 'glossary'
  id: string
  title: string
  snippet: string
}

export interface RulesSearchResponse {
  items: RulesSearchItem[]
}

// --- Deck tokens (POST /decks/tokens) ---

export type DeckTokenKind = 'token' | 'copy' | 'emblem' | 'dungeon' | 'designation'

/** One thing a deck brings to the table besides its cards: a token, an
 *  emblem, a dungeon, a designation (The Monarch), or a copy of a creature
 *  (kind `copy`, which has no image of its own). `sources` names every card
 *  in the deck that makes it. */
export interface DeckToken {
  key: string
  name: string
  type_line: string
  kind: DeckTokenKind
  scryfall_id: string | null
  image_url: string | null
  sources: string[]
  /** Scryfall's printed power/toughness; absent from older API builds. */
  power?: string | null
  toughness?: string | null
  colors?: string[]
}

export interface DeckTokensRequest {
  decklist: string
  commander: string | null
  partner: string | null
}

/** Tokens arrive already sorted; render them in order. */
export interface DeckTokensResponse {
  tokens: DeckToken[]
  card_count: number
  unresolved: number
}
